/**
 * autismDDRouting.js
 *
 * Pure helpers for the Adult Autism Diagnostic Decision (DD / "MDT Review")
 * module. These centralise three concerns so the four DD pages, the sidebar,
 * the Patient Overview card and the unit tests all agree:
 *
 *   1. Which differentials are "on the table" for the Other Conditions step
 *      (the de-duplicated UNION of what each clinician selected at the end of
 *      the CI+SSI appointment and the Observation appointment).
 *   2. When each step is valid/complete (so the sidebar can lock/unlock steps
 *      and the resume route can jump the clinician to the right page).
 *   3. The progress fraction written to the stage doc so the dashboard pill
 *      updates as the clinician works through the module.
 *
 * The DD module is built on the modular subcollection data model, so everything
 * here reads from `assessmentStages` (the object exposed by
 * ModularPatientDataContext), NOT the legacy monolithic `dataModel`.
 */

import { ASD_CONDITION_LABEL } from '../constants/autismConditions';

// ─── Route helpers ──────────────────────────────────────────────────────────
// All four step routes live under a single base path. Keeping the base in one
// place means appointmentStageRouting, routes.js and the pages cannot drift.

/** Builds the DD module base path for a patient (no trailing slash). */
export const getAutismDDBasePath = (patientId) =>
  `/patient/${patientId}/proforma/adult-asd/diagnostic-decision`;

/**
 * Returns the absolute path for each DD step, keyed by step id. Used by the
 * pages (CTA navigation), the sidebar (navigateToSection) and routes.js.
 */
export const getAutismDDStepRoutes = (patientId) => {
  const base = getAutismDDBasePath(patientId);
  return {
    mdtReview: `${base}/mdt-review`,
    autismDecision: `${base}/autism-decision`,
    otherConditions: `${base}/other-conditions`,
    finalFormulation: `${base}/final-formulation`,
  };
};

// ─── Differentials union ────────────────────────────────────────────────────

const toConditionArray = (value) => (Array.isArray(value) ? value : []);

/**
 * Returns the de-duplicated union of the emerging differentials selected by the
 * clinicians at the SSI Review and the Observation Review.
 *
 * Why a union: the two appointments are conducted by (potentially) different
 * clinicians, each of whom records their own emerging differentials. At the MDT
 * the team decides on the COMBINED set, so every distinct condition flagged in
 * either appointment must appear exactly once on the Other Conditions step.
 *
 * Ordering is deterministic and stable: SSI selections first (in their saved
 * order), then any OBS-only selections appended in their saved order. This keeps
 * the Other Conditions cards from re-shuffling between renders. ASD is filtered
 * out defensively — it is the condition referred for, never a "differential".
 *
 * @param {Object|undefined} assessmentStages - The modular stages object.
 * @returns {string[]} Unique condition labels (ASD excluded).
 */
export function getCombinedEmergingDifferentials(assessmentStages) {
  const ssiConditions = toConditionArray(
    assessmentStages?.semiStructuredInterview?.review?.formulationConditions,
  );
  const obsConditions = toConditionArray(
    assessmentStages?.observation?.review?.formulationConditions,
  );

  const seen = new Set();
  const union = [];
  for (const condition of [...ssiConditions, ...obsConditions]) {
    if (!condition || condition === ASD_CONDITION_LABEL) continue;
    if (seen.has(condition)) continue;
    seen.add(condition);
    union.push(condition);
  }
  return union;
}

// ─── Validity / completeness ────────────────────────────────────────────────

/**
 * The Autism decision is "valid" once an outcome has been chosen. The A-E
 * criteria toggles are captured for the record and surfaced on the review, but
 * they are optional — the single Met/Not met outcome is what drives the report.
 *
 * @param {Object|undefined} autismDecision - data.autismDecision
 * @returns {boolean}
 */
export function isAutismDecisionValid(autismDecision) {
  const outcome = autismDecision?.outcome;
  return outcome === 'Met' || outcome === 'Not met';
}

/**
 * Whether every condition in the differentials union has had a decision
 * recorded on the Other Conditions step. An empty union counts as complete
 * (there is nothing to decide).
 *
 * @param {Object|undefined} otherConditions - data.otherConditions
 * @param {string[]} union - getCombinedEmergingDifferentials(...)
 * @returns {boolean}
 */
export function isOtherConditionsComplete(otherConditions, union) {
  if (!Array.isArray(union) || union.length === 0) return true;
  const decided = otherConditions?.finalComorbidDiagnoses || {};
  return union.every((condition) => Boolean(decided[condition]?.decision));
}

/**
 * Whether the final formulation has any content. The formulation is a
 * structured-notes array (`[{ id, text, type }]`); it counts as present when at
 * least one note has non-whitespace text.
 *
 * @param {Array<{text?: string}>|undefined} finalFormulationNotes
 * @returns {boolean}
 */
export function isFinalFormulationComplete(finalFormulationNotes) {
  if (!Array.isArray(finalFormulationNotes)) return false;
  return finalFormulationNotes.some(
    (note) => note && typeof note.text === 'string' && note.text.trim(),
  );
}

// ─── Progress ───────────────────────────────────────────────────────────────

/**
 * Computes DD progress as a fraction (0..1) based on the actionable steps.
 *
 * When there are emerging differentials to decide (non-empty union) the module
 * has three steps, each worth 1/3:
 *   1/3 — the Autism outcome (Met/Not met) is set
 *   1/3 — every Other Condition is decided
 *   1/3 — a non-empty Final Formulation has been written
 *
 * When the union is empty the Other Conditions step does not exist in the
 * sidebar or the flow, so the denominator drops to 2:
 *   1/2 — the Autism outcome is set
 *   1/2 — a non-empty Final Formulation has been written
 *
 * Without this dynamic denominator, an empty union would vacuously satisfy the
 * Other Conditions check and report 33 % progress before the module is started.
 *
 * A completed stage always reports 1 regardless of the steps (the Finish flow
 * forces progress to 1). The dashboard reads this stored fraction directly.
 *
 * @param {Object} params
 * @param {Object} params.data - assessmentStages.diagnosticDecision.data
 * @param {string[]} params.union - getCombinedEmergingDifferentials(...)
 * @param {string} [params.status] - stage status; 'completed' forces 1
 * @returns {number} Progress fraction between 0 and 1
 */
export function computeAutismDDProgress({ data, union, status, skipDifferentialsDecisions = false }) {
  if (status === 'completed') return 1;
  const d = data || {};
  // When skipDifferentialsDecisions is true (TOC), the Other Conditions step
  // does not exist so it never counts toward progress.
  const hasOtherConditions = !skipDifferentialsDecisions && Array.isArray(union) && union.length > 0;
  let done = 0;
  let total = 2; // Autism Decision + Final Formulation always count

  if (isAutismDecisionValid(d.autismDecision)) done += 1;
  if (hasOtherConditions) {
    total += 1;
    if (isOtherConditionsComplete(d.otherConditions, union)) done += 1;
  }
  if (isFinalFormulationComplete(d.finalFormulationNotes)) done += 1;
  return done / total;
}

// ─── Resume routing ─────────────────────────────────────────────────────────

/**
 * Computes which DD step the "Start / Continue" action should land on, given
 * the current persisted state. The clinician always begins at the MDT Review
 * while the decision has not yet been made; once it has, we resume them at the
 * first incomplete downstream step.
 *
 * @param {Object} params
 * @param {string} params.patientId
 * @param {Object|undefined} params.assessmentStages - modular stages object
 * @returns {string} Absolute route path for the resume step
 */
export function getAutismDDStartRoute({ patientId, assessmentStages, skipDifferentialsDecisions = false }) {
  const routes = getAutismDDStepRoutes(patientId);
  const stage = assessmentStages?.diagnosticDecision;
  const data = stage?.data || {};
  const union = getCombinedEmergingDifferentials(assessmentStages);
  // When skipDifferentialsDecisions is true (TOC), never resume at the
  // Other Conditions page — it does not exist in this workflow.
  const hasOther = !skipDifferentialsDecisions && union.length > 0;

  // Decision not made yet → begin at the MDT Review (the module's entry point).
  if (!isAutismDecisionValid(data.autismDecision)) return routes.mdtReview;

  // Decision made; if there are other conditions still to decide, resume there.
  if (hasOther && !isOtherConditionsComplete(data.otherConditions, union)) {
    return routes.otherConditions;
  }

  // Otherwise resume at the Final Formulation.
  return routes.finalFormulation;
}

// ─── Sidebar sections ───────────────────────────────────────────────────────

export const AUTISM_DD_STEP_TITLES = {
  mdtReview: 'MDT Review',
  autismDecision: 'Decision for Autism',
  otherConditions: 'Decisions for Other Conditions',
  finalFormulation: 'Final Formulation',
};

/**
 * Maps a DDSideNavbar section index to its route, accounting for whether the
 * "Decisions for Other Conditions" step is present. When the differentials union
 * is empty that step is omitted, so Final Formulation shifts from index 3 to 2.
 *
 * @param {string} patientId
 * @param {number} index - zero-based sidebar index
 * @param {boolean} hasOther - whether the Other Conditions step is present
 * @returns {string} absolute route path
 */
export function getAutismDDRouteForSectionIndex(patientId, index, hasOther) {
  const routes = getAutismDDStepRoutes(patientId);
  const ordered = hasOther
    ? [routes.mdtReview, routes.autismDecision, routes.otherConditions, routes.finalFormulation]
    : [routes.mdtReview, routes.autismDecision, routes.finalFormulation];
  return ordered[index] || routes.mdtReview;
}

/**
 * Builds the DDSideNavbar `sections` array for the Adult Autism DD module.
 *
 * Steps:
 *   0. MDT Review                 — always unlocked. Marked complete once the
 *                                   clinician has moved past it (currentSectionIndex
 *                                   > 0) or the whole stage is complete.
 *   1. Decision for Autism        — always unlocked. Complete when an outcome is set.
 *   2. Decisions for Other Conditions — ONLY present when the differentials union
 *                                   is non-empty. Unlocked once the Autism decision
 *                                   is valid. Complete when all conditions decided.
 *   3. Final Formulation          — always present (final step). Unlocked once the
 *                                   Autism decision is valid. Complete when the
 *                                   formulation text is non-empty.
 *
 * Indices therefore depend on whether the Other Conditions step is present —
 * exactly mirroring the ADHD buildDDNavSections behaviour so the shared
 * DDSideNavbar renders identically.
 *
 * @param {Object} params
 * @param {Object|undefined} params.assessmentStages - modular stages object
 * @param {number} params.currentSectionIndex - zero-based active page index
 * @returns {Array<{sectionTitle: string, isUnlocked: boolean, status: string}>}
 */
export function buildAutismDDNavSections({ assessmentStages, currentSectionIndex, skipDifferentialsDecisions = false }) {
  const stage = assessmentStages?.diagnosticDecision;
  const data = stage?.data || {};
  const stageComplete = stage?.status === 'completed';
  const union = getCombinedEmergingDifferentials(assessmentStages);
  // When skipDifferentialsDecisions is true (TOC), the Other Conditions step
  // is removed from the sidebar — emerging differentials are shown as a
  // read-only list on the Formulation page instead.
  const hasOther = !skipDifferentialsDecisions && union.length > 0;

  const decisionValid = isAutismDecisionValid(data.autismDecision);
  const otherComplete = isOtherConditionsComplete(data.otherConditions, union);
  const formulationComplete = isFinalFormulationComplete(data.finalFormulationNotes);

  const sections = [
    {
      sectionTitle: AUTISM_DD_STEP_TITLES.mdtReview,
      isUnlocked: true,
      status: currentSectionIndex > 0 || stageComplete ? 'completed' : 'notStarted',
    },
    {
      sectionTitle: AUTISM_DD_STEP_TITLES.autismDecision,
      isUnlocked: true,
      status: decisionValid ? 'completed' : 'notStarted',
    },
  ];

  if (hasOther) {
    sections.push({
      sectionTitle: AUTISM_DD_STEP_TITLES.otherConditions,
      isUnlocked: decisionValid,
      status: decisionValid && otherComplete ? 'completed' : 'notStarted',
    });
  }

  sections.push({
    sectionTitle: AUTISM_DD_STEP_TITLES.finalFormulation,
    // The formulation "brings it all together", so it only unlocks once the
    // Autism decision is valid AND (when other conditions exist) every one has
    // been decided. A completed stage stays navigable via `stageComplete`. With
    // no other conditions it unlocks from the Autism decision alone, as before.
    isUnlocked: decisionValid && (!hasOther || otherComplete || stageComplete),
    status: formulationComplete ? 'completed' : 'notStarted',
  });

  return sections;
}

/**
 * The DD step ids in display order. The "Other Conditions" step only exists when
 * there are differentials to decide, so the array is 3 or 4 long. Index in this
 * array is the same index DDSideNavbar/navigateToSection use.
 *
 * @param {boolean} hasOther - whether the Other Conditions step is present
 * @returns {string[]} ordered step ids
 */
export function getAutismDDOrderedStepIds(hasOther) {
  return hasOther
    ? ['mdtReview', 'autismDecision', 'otherConditions', 'finalFormulation']
    : ['mdtReview', 'autismDecision', 'finalFormulation'];
}

/**
 * Adapts buildAutismDDNavSections() into the generic item shape consumed by the
 * shared <ModularSideNavbar>. Each step becomes a numbered badge; the DD
 * "completed"/"notStarted" status maps to the shared "completed"/"default"
 * (the shared sidebar also derives the active/started look from activeId).
 *
 * Returning `orderedIds` lets the page translate the sidebar's onSelect(id) back
 * into the index that navigateToSection() expects, with no behaviour change.
 *
 * @param {Object} params
 * @param {Object|undefined} params.assessmentStages - modular stages object
 * @param {number} params.currentSectionIndex - zero-based active page index
 * @returns {{items: Array, orderedIds: string[], activeId: string}}
 */
export function buildAutismDDNavItems({ assessmentStages, currentSectionIndex, skipDifferentialsDecisions = false }) {
  const union = getCombinedEmergingDifferentials(assessmentStages);
  const hasOther = !skipDifferentialsDecisions && union.length > 0;
  const orderedIds = getAutismDDOrderedStepIds(hasOther);
  const sections = buildAutismDDNavSections({ assessmentStages, currentSectionIndex, skipDifferentialsDecisions });

  const items = sections.map((section, index) => ({
    id: orderedIds[index],
    badge: index + 1,
    label: section.sectionTitle,
    status: section.status === 'completed' ? 'completed' : 'default',
    isUnlocked: section.isUnlocked,
  }));

  return { items, orderedIds, activeId: orderedIds[currentSectionIndex] };
}
